// BI埋点
// 1. 启动埋点（设备、用户）
// 2. 事件埋点（点击、浏览， 每次的属性+设备ID、用户ID）
// 3. 用户属性变更埋点（名称、手机号、邮箱）
// 4. 用户未登录使用GUID生成， 登录后使用用户ID
// 5. 用户登录后更新userId、登出后更新userInfo

import domain from '../server-config/domain.config'
import { $http } from '@/api/request'
import { v4 as uuidv4 } from 'uuid'

// 页面浏览埋点业务类型匹配
const pageViewList = [
  {
    type: 'home',
    path: ['/', '']
  },
  {
    type: 'search',
    path: ['/search']
  },
  {
    type: 'newArrival',
    path: ['/new-arrival']
  },
  {
    type: 'flashSale',
    path: ['/flash-sales']
  },
  {
    type: 'messageCenter',
    path: ['/message']
  },
  {
    type: 'cart',
    path: ['/shopping-cart']
  },
  {
    type: 'accountCenter',
    path: ['/user-center']
  },
  {
    type: 'addressList',
    path: ['/address']
  },
  {
    type: 'addEditAddress',
    path: ['/address/add'],
  },
  {
    type: 'addEditAddress',
    path: ['/address/'],
    key: 'addressId'
  },
  {
    type: 'productFavoriteList',
    path: ['/wish-list']
  },
  {
    type: 'storeFollowerList',
    path: ['/favorite-store']
  },
  {
    type: 'historyViewList',
    path: ['/recent-views']
  },
  {
    type: 'coinCenter',
    path: ['/coins','/account/coinsInfo']
  },
  {
    type: 'passwordLoginSignUp',
    path: ['/login'] // 手机号、邮箱注册单独判断， url没改变
  },
  {
    type: 'orderSubmit',
    path: ['/checkout']
  },
  {
    type: 'allCategoriesNavigation',
    path: ['/all-category']
  },
  {
    type: 'voucherList',
    path: ['/voucher']
  },
  {
    type: 'voucherCenter',
    path: ['/all-category']
  },
  {
    type: 'voucherList',
    path: ['/voucher']
  },
  {
    type: 'pendingReviewProductsList',
    path: ['/all-category']
  },
  {
    type: 'voucherList',
    path: ['/all-category']
  },
  {
    type: 'pendingReviewProductsList',
    path: ['/reviews-publish']
  }
]

// webClick点击埋点类型
const webClickTypeMap = {
  1: {
    element_business_type: 'clickAddToCart',
    page_type: 'productDetailPage'
  },
  2: {
    element_business_type: 'clickOrderNow',
    page_type: 'productDetailPage'
  },
  101: {
    element_business_type: 'confirmAddToCart',
    page_type: 'productDetailPage'
  },
  201: {
    element_business_type: 'confirmOrderNow',
    page_type: 'productDetailPage'
  },
  3: {
    element_business_type: 'clickShareProduct',
    page_type: 'productDetailPage'
  },
  4: {
    element_business_type: 'clickAddNewAddress',
    page_type: 'addressList'
  },
  5: {
    element_business_type: 'clickSendCode',
    page_type: 'smsLoginSignUp'
  },
  6: {
    element_business_type: 'clickSendCode',
    page_type: 'emailLoginSignUp'
  },
  7: {
    element_business_type: 'clickFacebookLogin',
    page_type: 'passwordLoginSignUp'
  },
  8: {
    element_business_type: 'clickFacebookLogin',
    // page_type: 'smsLoginSignUp'
    page_type: 'loginSignUpEmailOrSms' // 统一改成 loginSignUpEmailOrSms
  },
  9: {
    element_business_type: 'clickFacebookLogin',
    // page_type: 'emailLoginSignUp'
    page_type: 'loginSignUpEmailOrSms' // 统一改成 loginSignUpEmailOrSms
  },
  10: {
    element_business_type: 'clickGoogleLogin',
    page_type: 'passwordLoginSignUp'
  },
  11: {
    element_business_type: 'clickGoogleLogin',
    // page_type: 'smsLoginSignUp',
    page_type: 'loginSignUpEmailOrSms' // 统一改成 loginSignUpEmailOrSms
  },
  12: {
    element_business_type: 'clickGoogleLogin',
    // page_type: 'emailLoginSignUp'
    page_type: 'loginSignUpEmailOrSms' // 统一改成 loginSignUpEmailOrSms
  },
  13: {
    element_business_type: 'clickPlaceOrder',
    page_type: 'orderSubmit'
  },
  14: {
    element_business_type: 'clickCheckOut',
    page_type: 'cart'
  }
}

// 定义事件数据的接口
interface IEventData {
  event_version: string;
  channel: string;
  session_id: string | null;
  event_ts: number;
  app_type: string;
  app_version: string;
  anonymous_id: string | null;
  user_id: string | null;
  device_id: string | null;
  params: {
    _region_code: string;
    _src_page: string | null;
    _page: string | null;
    [key: string]: any;
  };
  [key: string]: any;
}
// 新BI埋点数据格式, 必须字段定义
const biEventData: IEventData = {
  event_version: '1.0.0',
  channel: '',
  session_id: '', // 临时会话ID， 关闭浏览器后重新生成
  event_ts: 0, // 13位毫秒数
  app_type: 'web',
  app_version: '3.0.0',
  anonymous_id: '', // 匿名ID
  user_id: null, // 用户ID
  device_id: null, // 设备ID
  params: {
    _region_code: '',
    _src_page: null,
    _page: null,
  }
}


// 事件Properties属性
const propertiesAttr = {
  user_id: '',
  device_id: ''
}

// 给后端sensors-properties加上BI埋点属性
export const getBiPropertiesAttr = () => {
  return {
    event_ts: Date.now(),
    app_type: biEventData.app_type,
    app_version: biEventData.app_version,
    anonymous_id: biEventData.anonymous_id,
    session_id: biEventData.session_id,
    user_id: biEventData.user_id,
    device_id: biEventData.device_id,
    channel: biEventData.channel,
    _region_code: biEventData.params._region_code,
    _src_page: biEventData.params._src_page,
    _page: biEventData.params._page,
  }
}

// 更新用户信息埋点
export const upDateUserInfoFn = () => {
  biEventData.user_id = useUserInfo().value?.userId
}

// 页面点击埋点
type WebClickTypeKey = keyof typeof webClickTypeMap;
export const webClickFn = (typeIndex: WebClickTypeKey, elementName: string, verificationStatus: boolean, failureType?: string, failureReason?: string) => {
  const webClickTypeInfo = webClickTypeMap[typeIndex]
  const webClickParams = {
    element_id: typeIndex,
    element_business_type: webClickTypeInfo.element_business_type,
    // page_type: webClickTypeInfo.page_type,  // 新BI埋点直接取_page
    element_name: elementName,
    verification_status: verificationStatus,
    failure_type: failureType || '',
    failureReason: failureReason || ''
  }
  trackBiEvent(eventName.webClick, webClickParams, propertiesAttr)
}

// 搜索埋点
export interface SearchEventParams {
  // 搜索来源模块（2026-01-27 起新命名规范）
  internal_latest_traffic_source_module?:
    | 'searchCategory'   // 搜索类目（原 category）
    | 'searchProduct'    // 搜索商品（原 search）
    | 'searchBrand'      // 搜索品牌（原 brand）
    | 'inStoreSearch'    // 在店铺内搜索
    | 'searchStore'      // 搜索店铺
    | 'Banner';          // 首页/频道 Banner 跳转搜索
  // 搜索来源类型；hotCategory 为业务历史值，暂时兼容保留
  internal_latest_traffic_source_type?:
    | 'enterSearch'              // 手动输入搜索
    | 'historySearch'            // 历史搜索
    | 'relatedSearch'            // 联想下拉补全搜索
    | 'hotSearch'                // 热搜搜索
    | 'correctionSearch'         // 纠错搜索
    | 'labelSearch'              // 标签搜索
    | 'rotatingSuggestionSearch' // 滚动建议搜索
    | 'hotCategory';             // 兼容保留：首页热门分类点击
  inside_latest_traffic_source_value?: string; // 搜索词或类目名
  is_has_result: boolean; // 是否有搜索结果
  result_nums: number; // 搜索结果商品数量
  is_has_filter: boolean; // 是否使用了过滤器
  sort_type: 'bestMatch' | 'latest' | 'topSales' | 'priceLowToHigh' | 'priceHighToLow'; // 排序类型
  request_id?: string;
  algorithm_model_name?: string;
}
type SensorsTrackFn = ReturnType<typeof import('./sensors.client').sensorsTrack>
const SENSORS_SEARCH_EVENT = 'search' as Parameters<SensorsTrackFn>[0]

export const searchEventFn = (params: SearchEventParams, sensorsTrack?: SensorsTrackFn) => {
  if (!process.client) return

  const searchParams = {
    internal_latest_traffic_source_module: params.internal_latest_traffic_source_module || 'searchProduct',
    internal_latest_traffic_source_type: params.internal_latest_traffic_source_type || 'enterSearch',
    inside_latest_traffic_source_value: params.inside_latest_traffic_source_value || '',
    is_has_result: params.is_has_result !== undefined ? params.is_has_result : true,
    result_nums: params.result_nums || 0,
    is_has_filter: params.is_has_filter !== undefined ? params.is_has_filter : false,
    sort_type: params.sort_type || 'bestMatch',
    request_id: params.request_id || '',
    algorithm_model_name: params.algorithm_model_name || ''
  }
  trackBiEvent(eventName.Search, searchParams, propertiesAttr)

  // 同步触发神策 search（字段下划线 → 驼峰）
  // 由调用方传入 setup 阶段取得的函数，避免异步回调中重新获取 Nuxt 上下文
  try {
    if (typeof sensorsTrack === 'function') {
      sensorsTrack(SENSORS_SEARCH_EVENT, {
        internalLatestTrafficSourceModule: searchParams.internal_latest_traffic_source_module,
        internalLatestTrafficSourceType: searchParams.internal_latest_traffic_source_type,
        insideLatestTrafficSourceValue: searchParams.inside_latest_traffic_source_value,
        isHasResult: searchParams.is_has_result,
        resultNums: searchParams.result_nums,
        isHasFilter: searchParams.is_has_filter,
        sortType: searchParams.sort_type,
        requestId: searchParams.request_id,
        algorithmModelName: searchParams.algorithm_model_name
      })
    }
  } catch (e) {
    warnTrackingFailureOnce('sensors-search', e)
  }
}
// 自定义埋点
export const customEventFn = (name: eventName, eventParams: any, propertiesParams?: any) => {
  if (propertiesParams) {
    // 合并 propertiesParams 到 propertiesAttr
    Object.assign(propertiesAttr, propertiesParams);
  }
  trackBiEvent(name, eventParams, propertiesAttr)
}

// 页面浏览埋点
const pageViewFn = (pageItem: any) => {
  const pageParams = {
    page_id: '-1',
    page_title: '',
    page_bar: 'default',
    url_path: window.location.href,
    page_entry_start_time: Math.floor(Date.now() / 1000)
  }
  if (pageItem) {
    pageParams.page_id = pageItem.pageId
    biEventData.params._page = pageItem.type // 当前页面的key
  } else {
    biEventData.params._page = 'unknownPage'
  }
  if (sessionStorage.getItem('bi_previous_page_key')) {
    // 上个页面的key
    biEventData.params._src_page = sessionStorage.getItem('bi_previous_page_key') || null
  }
  
  trackBiEvent(eventName.pageView, pageParams, propertiesAttr)
  // 把当前页面的page_type值存储到sessionStorage作为上个页面的key， 不为unknownPage则存储
  if (biEventData.params._page && biEventData.params._page !== 'unknownPage') {
    sessionStorage.setItem('bi_previous_page_key', biEventData.params._page)
  }
}

// 初始化启动埋点
const startFn = (pageItem: any) => {
  // 清除sessionStorage中的上个页面的key
  sessionStorage.removeItem('bi_previous_page_key')
  // 用户ID
  if (useUserInfo().value?.userId) {
    biEventData.user_id = useUserInfo().value?.userId
  }
  const regionId = useCurrentCountry().value
  const countryInfo = useCountrys().value.find((c: region.IRegionItem) => c.id === regionId)! || {}
  biEventData.params._region_code = countryInfo.code || 'KE'
  // 业务域
  biEventData.channel = useIsMobile().value ? 'ch_mall_buyer_web_mobile' : 'ch_mall_buyer_web_pc'
  // 匿名ID, 存储Cookie 和 商城同步 Pfx-Anon-Sid请求头参数
  if (!useCookie('anon_sid').value) {
    biEventData.anonymous_id = uuidv4()
    useCookie('anon_sid').value = biEventData.anonymous_id
  } else {
    biEventData.anonymous_id = useCookie('anon_sid').value || null
  }
  // 参考APP这2个用一样的
  biEventData.device_id = biEventData.anonymous_id
  // 会话ID， 存储sessionStorage
  if (!sessionStorage.getItem('bi_session_id')) {
    biEventData.session_id = uuidv4()
    sessionStorage.setItem('bi_session_id', biEventData.session_id)

    // 新会话开始时上报会话环境信息
    reportSessionEnv()
  } else {
    biEventData.session_id = sessionStorage.getItem('bi_session_id') || null
  }

  // 浏览埋点
  pageViewFn(pageItem)
  // 上报设备信息（每天一次）
  if (process.client) {
    reportDeviceInfo()
  }
}

// 退出登录重置userId
export const logoutResetUserIdFn = () => {
  biEventData.user_id = null
}

// 页面Tab切换埋点
export const tabPageViewFn = (tabPageKey: string) => {
  const tabPageViewMap = {
    voucherCenter: 101,
    productFollowerList: 11,
    storeFollowerList: 11,
    loginSignUpEmailOrSms: 102,
    addEditAddress: 9,
    reviewedProductsList: 103
  }
  const pageItem = {
    pageId: tabPageViewMap[tabPageKey],
    type: tabPageKey
  }
  pageViewFn(pageItem)
}

// 属性初始化
const attrInit = (pageItem : any) => {
  // 用户首次访问时间
  if (!localStorage.getItem('bi_user_first_visit_time')) {
    localStorage.setItem('bi_user_first_visit_time', (Math.floor(Date.now() / 1000).toString()))
  }
  const getVisitorId = async() => {
    // biEventData.device_id = visitorId
    // 初始化启动埋点
    startFn(pageItem)
  }
  // 判断是否已存在浏览器指纹， 没有则生成
  if (!biEventData.device_id) {
    getVisitorId()
  } else {
    // 已存在走浏览埋点
    pageViewFn(pageItem)
  }
  
}
// 埋点事件名称定义
export enum eventName {
  pageView = 'pageView',
  webClick = 'webClick',
  registerSuccess = 'registerSuccess',
  loginSuccess = 'loginSuccess',
  addAddressSuccess = 'addAddressSuccess',
  editAddressSuccess = 'editAddressSuccess',
  exposureProduct = 'exposureProduct',
  viewProductDetail = 'viewProductDetail',
  addToCart = 'addToCart',
  addToFavorites = 'addToFavorites',
  submitOrderSuccess = 'submitOrderSuccess',
  payOrderSuccess = 'payOrderSuccess',
  Search = 'search',
  collectDeviceInfo = 'collectDeviceInfo',
  trackSessionEnv = 'trackSessionEnv',
  clickToShareButton = 'clickToShareButton',
  clickToShareChannel = 'clickToShareChannel'
}
type Properties = {
  user_id: string,
  device_id: string,
  [key: string]: any
}

type RunWithNuxtContext = ReturnType<typeof useNuxtApp>['runWithContext']

let runWithNuxtContext: RunWithNuxtContext | null = null
const warnedTrackingFailures = new Set<string>()

const warnTrackingFailureOnce = (source: string, error: unknown) => {
  if (warnedTrackingFailures.has(source)) return

  warnedTrackingFailures.add(source)
  console.warn(`[tracking] ${source} failed:`, error)
}

const trackBiEvent = (
  name: eventName,
  data: { [key:string]: any },
  properties: Properties,
  callback?: (res: any) => void
) => {
  if (!process.client || !runWithNuxtContext) return

  void postEventFn(name, data, properties, callback)
    .catch((error) => warnTrackingFailureOnce(`bi-${name}`, error))
}

// 请求后端上报埋点数据
const postEventFn = async (
  eventName: eventName,
  eventData: { [key:string]: any },
  properties: Properties,
  callback?: (res: any) => void
) => {
  // 旧版本埋点格式
  // const trackBody = {
  //   eventName,
  //   eventData,
  //   properties
  // }
  // // 表格中每次埋点都需要的属性
  // properties._time = Math.floor(Date.now() / 1000)
  // // 站外末次流量来源
  // const searchParams = new URLSearchParams(location.search)
  // const externalLastTrafficSourceType = searchParams.get('externalLastTrafficSourceType') || 'organic'
  // const externalLastTrafficSourceChannel = searchParams.get('externalLastTrafficSourceChannel') || (useIsMobile().value ? 'WAP' : 'PC')
  // const externalLastTrafficValue = searchParams.get('externalLastTrafficValue') || ''
  // properties._external_last_traffic_source_type = externalLastTrafficSourceType
  // properties._external_last_traffic_source_channel = externalLastTrafficSourceChannel
  // properties._externalLastTrafficValue = externalLastTrafficValue
  // if (localStorage.getItem('bi_user_first_visit_time')) {
  //   // 获取当前日期的时间戳（当天的00:00:00）
  //   const currentDate = new Date();
  //   currentDate.setHours(0, 0, 0, 0);
  //   const currentDateTimestamp = currentDate.getTime() / 1000; // 转换为秒

  //   // 获取用户首次访问时间的时间戳
  //   const firstVisitTime: string = localStorage.getItem('bi_user_first_visit_time') || '0';
  //   const firstVisitTimestamp = parseInt(firstVisitTime, 10); // 转换为整数
  //   // 判断用户首次访问时间是否在当前日期内
  //   const isFirstVisitToday = firstVisitTimestamp >= currentDateTimestamp && firstVisitTimestamp < currentDateTimestamp + 86400; // 86400秒为一天
  //   if (isFirstVisitToday) {
  //     // 用户首次访问时间在当前日期内
  //     properties._is_first_day = true
  //   } else {
  //     // 用户首次访问时间不在当前日期内
  //     properties._is_first_day = false
  //   }
  // } else {
  //   properties._is_first_day = true
  // }  

  // 新版本埋点格式
  const postParams = JSON.parse(JSON.stringify(biEventData))
  postParams.event_ts = Date.now()
  postParams.event_name = eventName

  postParams.params = {
    ...postParams.params,
    ...eventData
  }
  postParams.params = JSON.stringify(postParams.params)
  
  await runWithNuxtContext!(() => $http.post({
      url: `${domain.BI_URL}/v2/event`,
      body: postParams,
      loading: false,
      server: false
    }))
    .then((res: any) => {
      callback && callback(res)
    })
}

// 获取设备信息并上报（每天一次）
export const reportDeviceInfo = () => {
  if (!process.client) return;
  
  // 检查今天是否已经上报过
  const lastReportDate = localStorage.getItem('bi_device_info_last_report');
  const today = new Date().toISOString().split('T')[0]; // 格式：YYYY-MM-DD
  
  if (lastReportDate === today) {
    // 今天已经上报过，不再上报
    return;
  }
  
  // 获取设备信息
  const deviceInfo = {
    brand: '',
    model: '',
    os: '',
    os_version: '',
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    install_source: null
  };
  
  // 获取操作系统信息
  const userAgent = navigator.userAgent;
  
  // 操作系统检测
  if (/Windows/i.test(userAgent)) {
    deviceInfo.os = 'Windows';
    const matches = userAgent.match(/Windows NT (\d+\.\d+)/);
    deviceInfo.os_version = matches ? matches[1] : '';
  } else if (/Macintosh/i.test(userAgent)) {
    deviceInfo.os = 'MacOS';
    const matches = userAgent.match(/Mac OS X (\d+[._]\d+[._]?\d*)/);
    deviceInfo.os_version = matches ? matches[1].replace(/_/g, '.') : '';
  } else if (/Android/i.test(userAgent)) {
    deviceInfo.os = 'Android';
    const matches = userAgent.match(/Android (\d+\.\d+(\.\d+)?)/);
    deviceInfo.os_version = matches ? matches[1] : '';
  } else if (/iPhone|iPad|iPod/i.test(userAgent)) {
    deviceInfo.os = 'iOS';
    const matches = userAgent.match(/OS (\d+[._]\d+[._]?\d*)/);
    deviceInfo.os_version = matches ? matches[1].replace(/_/g, '.') : '';
  } else if (/Linux/i.test(userAgent)) {
    deviceInfo.os = 'Linux';
    deviceInfo.os_version = '';
  } else {
    deviceInfo.os = 'Unknown';
    deviceInfo.os_version = '';
  }
  
  // 设备品牌和型号检测
  if (/iPhone/i.test(userAgent)) {
    deviceInfo.brand = 'Apple';
    const matches = userAgent.match(/iPhone(\d+,\d+)/i);
    deviceInfo.model = matches ? `iPhone ${matches[1]}` : 'iPhone';
  } else if (/iPad/i.test(userAgent)) {
    deviceInfo.brand = 'Apple';
    deviceInfo.model = 'iPad';
  } else if (/Android/i.test(userAgent)) {
    // 尝试从用户代理获取Android设备信息
    const matches = userAgent.match(/Android.*?;\s([^;]+)\s+Build/i);
    if (matches) {
      const deviceInfoStr = matches[1].trim();
      // 尝试分离品牌和型号
      const brandModelMatch = deviceInfoStr.match(/^(\w+)\s+(.+)$/);
      if (brandModelMatch) {
        deviceInfo.brand = brandModelMatch[1];
        deviceInfo.model = brandModelMatch[2];
      } else {
        deviceInfo.brand = 'Android';
        deviceInfo.model = deviceInfoStr;
      }
    } else {
      deviceInfo.brand = 'Android';
      deviceInfo.model = 'Unknown';
    }
  } else {
    // 桌面设备
    if (/Macintosh/i.test(userAgent)) {
      deviceInfo.brand = 'Apple';
      deviceInfo.model = 'Mac';
    } else if (/Windows/i.test(userAgent)) {
      deviceInfo.brand = 'PC';
      deviceInfo.model = 'Windows';
    } else if (/Linux/i.test(userAgent)) {
      deviceInfo.brand = 'PC';
      deviceInfo.model = 'Linux';
    } else {
      deviceInfo.brand = 'Unknown';
      deviceInfo.model = 'Unknown';
    }
  }
  // 上报设备信息
  customEventFn(eventName.collectDeviceInfo, deviceInfo);
  
  // 记录今天已经上报过
  localStorage.setItem('bi_device_info_last_report', today);
}
// 上报会话环境信息
export const reportSessionEnv = () => {
  if (!process.client) return;
  
  const searchParams = new URLSearchParams(location.search)
  const externalLastTrafficSourceType = searchParams.get('externalLastTrafficSourceType') || 'organic'
  const externalLastTrafficSourceChannel = searchParams.get('externalLastTrafficSourceChannel') || (useIsMobile().value ? 'WAP' : 'PC')
  const externalLastTrafficValue = searchParams.get('externalLastTrafficValue') || ''

  // 获取会话环境信息
  const sessionEnvInfo = {
    timezone_offset: -new Date().getTimezoneOffset(), // 时区偏移，分钟数，东区为负数，西区为正数
    carrier: null, // 运营商，JS无法直接获取
    network_type: null, // 网络类型
    is_wifi: null, // 是否WiFi
    ip: null, // 用户IP
    longitude: null, // 经度
    latitude: null, // 纬度
    google_push_token: null, // Google推送令牌
    gaid: null, // Google广告标识符
    ext_tr_last_src_type: externalLastTrafficSourceType,
    ext_tr_last_src_channel: externalLastTrafficSourceChannel,
    ext_tr_last_src_value: externalLastTrafficValue
  }
  
  // 尝试获取网络连接信息
  if (navigator.connection) {
    const connection = navigator.connection as any;
    if (connection.type) {
      sessionEnvInfo.network_type = connection.type;
      sessionEnvInfo.is_wifi = connection.type === 'wifi';
    }
    
    // 尝试获取运营商信息
    if (connection.effectiveType) {
      sessionEnvInfo.carrier = connection.effectiveType;
    }
  }
  // 上报会话环境信息
  customEventFn(eventName.trackSessionEnv, sessionEnvInfo);
}

export default defineNuxtPlugin(async(NuxtApp) => {
  runWithNuxtContext = (callback) => NuxtApp.runWithContext(callback)
  const userInfo = useUserInfo()
  const route = useRoute()

  watch(
    () => userInfo.value?.userId,
    (userId) => {
      biEventData.user_id = userId || null
    },
    { immediate: true }
  )

  // 初始化埋点
  watch(() => route.path, (newPath) => {
    const pageItem = pageViewList.find((item, index) => item.path.some(pathItem => {
      item.pageId = index + 1
      if (item.key) {
        return newPath === pathItem + route.params[item.key]
      } else {
        return newPath === pathItem
      }
    }))
    attrInit(pageItem)
  }, { immediate: true })
  return {
    provide: {
     
    }
  }
})
